// Costs that aren't one fixed number.
//
// Most cards have a plain number in their cost cell. A few work their cost out as they are played, and
// their cost cell holds a small formula instead:
//
//   gerzite                      all the Gerzite you have right now        (Crystalline Discharge)
//   gerzite_spent                a number you choose as you play it        (Gerzite Dagger)
//   2 + cards_removed            2, plus a number you choose               (Curse of Vanishing)
//   round(discard_troops / 2)    half the Troops in your discard pile      (Dead Rising)
//   up(discard / 2) + 1          half your discard pile, rounded up, +1    (Primordial Seastorm)
//   last_card + 1                what your opponent's last card cost, +1   (Reflective Orb)
//   unit + 1                     the cost of a Unit you pick from your hand, +1   (Tribunal Trigger)
//
// What a formula can hold:
//   - numbers, + - * / and brackets
//   - round(…) to the nearest whole (halves go up), up(…) always up, down(…) always down
//   - the table's own numbers, listed in FACTS below
//   - ONE word of your own (gerzite_spent, health_removed, cards_removed…): that is a number the player
//     is asked for as the card is played, and the word is the question ("health removed?")
// A cost never comes out below 0, and a result that isn't whole is rounded to the nearest whole.

/** The numbers the table knows by itself, and what each one means (shown to the player). */
export const FACTS: Record<string, string> = {
  gerzite: 'all your Gerzite',
  discard: 'the cards in your discard pile',
  discard_troops: 'the Troops in your discard pile',
  last_card: 'what your opponent paid for their last card',
  unit: 'the cost of a Unit you pick from your hand',
};
const FUNCTIONS: Record<string, (n: number) => number> = {
  round: (n) => Math.floor(n + 0.5),
  up: (n) => Math.ceil(n),
  down: (n) => Math.floor(n),
};

/** What the table knows when a card is about to be played. */
export interface CostFacts {
  gerzite: number;
  discard: number;
  discardTroops: number;
  lastCard: number | null; // what your opponent paid for the last card they played face-up (null = none yet)
  lastDark: boolean; // …and whether they paid it in Dark Gerzite (the card's own cost, or a Darkgale summon)
  lastName: string; // …and its name
}

type Node = { t: 'num'; v: number } | { t: 'var'; name: string } | { t: 'op'; op: string; a: Node; b: Node } | { t: 'fn'; name: string; a: Node } | { t: 'neg'; a: Node };

export interface CostFormula {
  text: string; // as written in cards.csv
  uses: string[]; // the table's numbers it reads (keys of FACTS)
  choice: string | null; // the number the player is asked for ("health_removed"), if any
  tree: Node;
}

/** "health_removed" → "health removed" */
export const wordsOf = (name: string) => name.replace(/_/g, ' ');

/** Reads a cost cell that isn't a plain number. Returns the formula, or a sentence saying what is wrong with it. */
export function parseCostFormula(text: string): CostFormula | string {
  const src = text.trim().toLowerCase();
  const tokens = src.match(/\d+(?:\.\d+)?|[a-z_][a-z0-9_]*|[-+*/()]|\S/g) ?? [];
  let i = 0;
  const vars = new Set<string>();
  const fail = (why: string): never => {
    throw new Error(why);
  };
  const factor = (): Node => {
    const tk = tokens[i++];
    if (tk === undefined) return fail('it stops too soon');
    if (/^\d/.test(tk)) return { t: 'num', v: Number.parseFloat(tk) };
    if (tk === '-') return { t: 'neg', a: factor() };
    if (tk === '(') {
      const inner = expr();
      if (tokens[i++] !== ')') fail('a bracket is not closed');
      return inner;
    }
    if (/^[a-z_]/.test(tk)) {
      if (tokens[i] === '(') {
        if (!FUNCTIONS[tk]) fail(`"${tk}(" isn't one of round(, up( or down(`);
        i++;
        const inner = expr();
        if (tokens[i++] !== ')') fail('a bracket is not closed');
        return { t: 'fn', name: tk, a: inner };
      }
      vars.add(tk);
      return { t: 'var', name: tk };
    }
    return fail(`"${tk}" isn't something a cost can hold`);
  };
  const term = (): Node => {
    let a = factor();
    while (tokens[i] === '*' || tokens[i] === '/') a = { t: 'op', op: tokens[i++], a, b: factor() };
    return a;
  };
  const expr = (): Node => {
    let a = term();
    while (tokens[i] === '+' || tokens[i] === '-') a = { t: 'op', op: tokens[i++], a, b: term() };
    return a;
  };
  try {
    const tree = expr();
    if (i < tokens.length) fail(`"${tokens[i]}" is in the wrong place`);
    const choices = [...vars].filter((v) => !(v in FACTS));
    if (choices.length > 1) fail(`it asks the player for more than one number (${choices.join(', ')})`);
    return { text: src, uses: [...vars].filter((v) => v in FACTS), choice: choices[0] ?? null, tree };
  } catch (e) {
    return (e as Error).message;
  }
}

/**
 * Works a formula out. `choice` is the number the player gave (if the formula asks for one) and `unit` the
 * cost of the Unit they picked. Returns the cost, or a sentence saying what is still missing.
 */
export function priceOf(f: CostFormula, facts: CostFacts, choice?: number | null, unit?: number | null): number | string {
  if (f.choice && (choice === undefined || choice === null || !Number.isFinite(choice))) return `Choose a number first: ${wordsOf(f.choice)}.`;
  if (f.uses.includes('unit') && (unit === undefined || unit === null)) return 'Pick a Unit from your hand first: its cost sets this card’s cost.';
  if (f.uses.includes('last_card') && facts.lastCard === null) return 'Your opponent hasn’t played a card yet, so there is nothing to work this cost out from.';
  const values: Record<string, number> = { gerzite: facts.gerzite, discard: facts.discard, discard_troops: facts.discardTroops, last_card: facts.lastCard ?? 0, unit: unit ?? 0 };
  if (f.choice) values[f.choice] = Math.max(0, Math.round(choice!));
  const run = (n: Node): number => {
    if (n.t === 'num') return n.v;
    if (n.t === 'var') return values[n.name] ?? 0;
    if (n.t === 'neg') return -run(n.a);
    if (n.t === 'fn') return FUNCTIONS[n.name](run(n.a));
    const a = run(n.a);
    const b = run(n.b);
    return n.op === '+' ? a + b : n.op === '-' ? a - b : n.op === '*' ? a * b : b === 0 ? 0 : a / b;
  };
  const out = run(f.tree);
  return Number.isFinite(out) ? Math.max(0, Math.floor(out + 0.5)) : 0;
}

/** A formula in plain words, for the card's close-up: "2 + cards removed (you choose)". */
export function describeFormula(f: CostFormula): string {
  let text = f.text.replace(/\s+/g, ' ').replace(/\s*([+*/-])\s*/g, ' $1 ').replace(/\(\s+/g, '(').replace(/\s+\)/g, ')');
  for (const [key, meaning] of Object.entries(FACTS).sort((a, b) => b[0].length - a[0].length)) text = text.replace(new RegExp(`\\b${key}\\b`, 'g'), meaning);
  text = text.replace(/\bround\(([^()]+)\)/g, '$1, rounded').replace(/\bup\(([^()]+)\)/g, '$1, rounded up').replace(/\bdown\(([^()]+)\)/g, '$1, rounded down');
  if (f.choice) text = text.replace(new RegExp(`\\b${f.choice}\\b`, 'g'), `${wordsOf(f.choice)} (you choose)`);
  return text.replace(/ \/ 2\b/g, ' ÷ 2');
}
